import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useApi } from './ApiContext';
import { AutoOcoBracketModel, AutoOcoBracketRequest, AutoOcoBracketRemoveRequest, AutoOcoBracketErrorCode } from '@api/userApi';
import { logException } from '@/helpers/exceptionHelper';
import { handleAxiosErrorWithCallback } from '@/helpers/axiosHelper';
import { toast } from 'react-toastify';

interface OcoBracketsContextType {
  //Stateless call to get a bracket by id
  getBracket: (bracketId: number) => AutoOcoBracketModel | undefined;
  brackets: AutoOcoBracketModel[];
  loading: boolean;
  saving: boolean;
  deleteLoading: number | null;
  loadBrackets: () => Promise<void>;
  createBracket: (request: AutoOcoBracketRequest) => Promise<boolean>;
  updateBracket: (request: AutoOcoBracketRequest) => Promise<boolean>;
  deleteBracket: (bracketId: number) => Promise<boolean>;
  refreshBrackets: () => Promise<void>;
}

const OcoBracketsContext = createContext<OcoBracketsContextType | undefined>(undefined);

export const useOcoBrackets = () => {
  const context = useContext(OcoBracketsContext);
  if (context === undefined) {
    throw new Error('useOcoBrackets must be used within an OcoBracketsProvider');
  }
  return context;
};

interface OcoBracketsProviderProps {
  children: React.ReactNode;
}

export const OcoBracketsProvider: React.FC<OcoBracketsProviderProps> = ({ children }) => {
  const { autoOcoBracketsApi } = useApi();

  const [brackets, setBrackets] = useState<AutoOcoBracketModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);

  //Store a reference to the brackets so we can access them in a stateless way
  const bracketsRef = useRef<AutoOcoBracketModel[]>([]);

  useEffect(() => {
    bracketsRef.current = brackets;
  }, [brackets]);

  const loadBrackets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await autoOcoBracketsApi.get();
      setBrackets(response || []);
    } catch (error) {
      logException(error, 'Error loading Auto OCO brackets');
      toast.error('Failed to load Auto OCO brackets');
    } finally {
      setLoading(false);
    }
  }, [autoOcoBracketsApi]);

  const refreshBrackets = useCallback(async () => {
    try {
      const response = await autoOcoBracketsApi.get();
      setBrackets(response || []);
    } catch (error) {
      logException(error, 'Error refreshing Auto OCO brackets');
    }
  }, [autoOcoBracketsApi]);

  const createBracket = useCallback(
    async (request: AutoOcoBracketRequest): Promise<boolean> => {
      setSaving(true);
      try {
        const response = await autoOcoBracketsApi.post(request);

        if (response.success) {
          toast.success('Auto OCO bracket created successfully');
          await refreshBrackets();
          return true;
        } else {
          let errorMessage = response.errorMessage || 'Failed to create Auto OCO bracket';

          // Provide more specific error messages based on error codes
          switch (response.errorCode) {
            case AutoOcoBracketErrorCode.InvalidStopLossTicks:
              errorMessage = 'Invalid stop loss ticks value. Must be a positive number.';
              break;
            case AutoOcoBracketErrorCode.InvalidTakeProfitTicks:
              errorMessage = 'Invalid take profit ticks value. Must be a positive number.';
              break;
            case AutoOcoBracketErrorCode.InvalidStopLossType:
              errorMessage = 'Invalid stop loss type. Must be Stop or Trailing Stop.';
              break;
            case AutoOcoBracketErrorCode.InvalidTakeProfitType:
              errorMessage = 'Invalid take profit type. Must be Limit.';
              break;
            case AutoOcoBracketErrorCode.BracketAlreadyExists:
              errorMessage = 'A bracket with this name already exists.';
              break;
            case AutoOcoBracketErrorCode.NoBracketOrdersProvided:
              errorMessage = 'At least one of Stop Loss or Take Profit must be specified.';
              break;
            case AutoOcoBracketErrorCode.Unauthorized:
              errorMessage = 'You are not authorized to perform this action.';
              break;
          }

          toast.error(errorMessage);
          return false;
        }
      } catch (error) {
        handleAxiosErrorWithCallback(error, () => {
          logException(error, 'Error creating Auto OCO bracket');
        });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [autoOcoBracketsApi, refreshBrackets]
  );

  const updateBracket = useCallback(
    async (request: AutoOcoBracketRequest): Promise<boolean> => {
      setSaving(true);
      try {
        const response = await autoOcoBracketsApi.post(request);

        if (response.success) {
          toast.success('Auto OCO bracket updated successfully');
          await refreshBrackets();
          return true;
        } else {
          let errorMessage = response.errorMessage || 'Failed to update Auto OCO bracket';

          // Provide more specific error messages based on error codes
          switch (response.errorCode) {
            case AutoOcoBracketErrorCode.InvalidStopLossTicks:
              errorMessage = 'Invalid stop loss ticks value.';
              break;
            case AutoOcoBracketErrorCode.InvalidTakeProfitTicks:
              errorMessage = 'Invalid take profit ticks value.';
              break;
            case AutoOcoBracketErrorCode.InvalidStopLossType:
              errorMessage = 'Invalid stop loss type. Must be Stop or Trailing Stop.';
              break;
            case AutoOcoBracketErrorCode.InvalidTakeProfitType:
              errorMessage = 'Invalid take profit type. Must be Limit.';
              break;
            case AutoOcoBracketErrorCode.BracketAlreadyExists:
              errorMessage = 'A bracket with this name already exists.';
              break;
            case AutoOcoBracketErrorCode.NoBracketOrdersProvided:
              errorMessage = 'At least one of Stop Loss or Take Profit must be specified.';
              break;
            case AutoOcoBracketErrorCode.Unauthorized:
              errorMessage = 'You are not authorized to perform this action.';
              break;
          }

          toast.error(errorMessage);
          return false;
        }
      } catch (error) {
        handleAxiosErrorWithCallback(error, () => {
          logException(error, 'Error updating Auto OCO bracket');
        });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [autoOcoBracketsApi, refreshBrackets]
  );

  const deleteBracket = useCallback(
    async (bracketId: number): Promise<boolean> => {
      setDeleteLoading(bracketId);
      try {
        const request = new AutoOcoBracketRemoveRequest({
          bracketId: bracketId
        });

        const response = await autoOcoBracketsApi.delete(request);

        if (response.success) {
          toast.success('Auto OCO bracket deleted successfully');
          await refreshBrackets();
          return true;
        } else {
          toast.error(response.errorMessage || 'Failed to delete Auto OCO bracket');
          return false;
        }
      } catch (error) {
        handleAxiosErrorWithCallback(error, () => {
          logException(error, 'Error deleting Auto OCO bracket');
        });
        return false;
      } finally {
        setDeleteLoading(null);
      }
    },
    [autoOcoBracketsApi, refreshBrackets]
  );

  // Load brackets on mount
  useEffect(() => {
    loadBrackets();
  }, [loadBrackets]);

  const contextData = useMemo(() => {
    return {
      getBracket: (bracketId: number) => bracketsRef.current.find((x) => x.id === bracketId),
      brackets,
      loading,
      saving,
      deleteLoading,
      loadBrackets,
      createBracket,
      updateBracket,
      deleteBracket,
      refreshBrackets
    };
  }, [brackets, loading, saving, deleteLoading, loadBrackets, createBracket, updateBracket, deleteBracket, refreshBrackets]);

  return <OcoBracketsContext.Provider value={contextData}>{children}</OcoBracketsContext.Provider>;
};
